import { useState, useEffect } from 'react';
import { useAuth } from '../context/authContext';
import { supabase } from '../supabaseClient';
import { TrashIcon } from '@heroicons/react/24/solid';

export default function CreateModuleLesson() {
  const { user } = useAuth();

  const [title, setTitle] = useState('');
  const [level, setLevel] = useState('beginner');
  const [moduleId, setModuleId] = useState('');
  const [reading, setReading] = useState('');
  const [writingPrompt, setWritingPrompt] = useState('');
  const [quiz, setQuiz] = useState([]);
  const [newQuestion, setNewQuestion] = useState({ text: '', options: ['', '', ''], correct: '' });
  const [lessonSource, setLessonSource] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [modules, setModules] = useState([]);
  const [allowCaseBuilder, setAllowCaseBuilder] = useState(false);

  useEffect(() => {
    const fetchModules = async () => {
      if (!user?.isAdmin) return;
      const { data, error } = await supabase.from('modules').select('id, name');
      if (!error) setModules(data);
    };
    fetchModules();
  }, [user]);

  const handleGenerateLesson = async () => {
    if (!lessonSource) return;
    setGenerating(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/generate-lesson-from-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: lessonSource }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setReading(data.reading || '');
      setWritingPrompt(data.writing_prompt || '');
      setQuiz(data.quiz || []);
      setSuccess('Lesson generated from article.');
    } catch {
      setError('Could not generate lesson from URL.');
    } finally {
      setGenerating(false);
    }
  };

  const handleAddQuestion = () => {
    const options = newQuestion.options.filter(opt => opt.trim());
    if (options.length < 2 || new Set(options).size !== options.length || !options.includes(newQuestion.correct)) {
      alert('Each question must have at least 2 unique options, and the correct answer must match one.');
      return;
    }
    setQuiz([...quiz, { ...newQuestion, options }]);
    setNewQuestion({ text: '', options: ['', '', ''], correct: '' });
  };

  const handleSubmit = async () => {
    if (!title || !reading || !moduleId) return setError('Missing required fields');
    const { error: insertError } = await supabase.from('module_lessons').insert([{
      title,
      level,
      module_id: moduleId,
      reading,
      writing_prompt: writingPrompt || null,
      allow_writing: !!writingPrompt,
      allow_case_builder: allowCaseBuilder,
      quiz,
    }]);
    if (insertError) return setError(insertError.message);
    setSuccess('Module lesson saved!');
    setTitle('');
    setModuleId('');
    setReading('');
    setWritingPrompt('');
    setQuiz([]);
    setAllowCaseBuilder(false);
    setError('');
  };

  if (!user?.isAdmin) return <div className="p-6 text-gray-600">Access denied. Only admins can create module lessons.</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-10">
      <h2 className="text-3xl font-bold text-gray-800">Create a Module Lesson</h2>

      <div className="space-y-4">
        <label className="font-medium text-gray-700">Generate from Article URL</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={lessonSource}
            onChange={(e) => setLessonSource(e.target.value)}
            placeholder="Paste article URL"
            className="flex-1 p-3 border border-gray-300 rounded"
          />
          <button
            onClick={handleGenerateLesson}
            className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-400"
            disabled={generating}
          >
            {generating ? 'Generating...' : 'Generate'}
          </button>
        </div>
      </div>

      <input
        type="text"
        placeholder="Lesson Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full p-3 border border-gray-300 rounded"
      />

      <select
        value={moduleId}
        onChange={(e) => setModuleId(e.target.value)}
        className="w-full p-3 border border-gray-300 rounded"
      >
        <option value="">Select Module</option>
        {modules.map(mod => (
          <option key={mod.id} value={mod.id}>{mod.name}</option>
        ))}
      </select>

      <select
        value={level}
        onChange={(e) => setLevel(e.target.value)}
        className="w-full p-3 border border-gray-300 rounded"
      >
        <option value="beginner">Beginner</option>
        <option value="intermediate">Intermediate</option>
        <option value="advanced">Advanced</option>
      </select>

      <textarea
        rows="6"
        placeholder="Reading Material"
        value={reading}
        onChange={(e) => setReading(e.target.value)}
        className="w-full p-3 border border-gray-300 rounded"
      />

      <textarea
        rows="4"
        placeholder="Writing Prompt (optional)"
        value={writingPrompt}
        onChange={(e) => setWritingPrompt(e.target.value)}
        className="w-full p-3 border border-gray-300 rounded"
      />

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="caseBuilder"
          checked={allowCaseBuilder}
          onChange={(e) => setAllowCaseBuilder(e.target.checked)}
        />
        <label htmlFor="caseBuilder" className="text-sm text-gray-700">Allow Case Builder</label>
      </div>

      <div className="space-y-6">
        <h3 className="text-xl font-semibold text-gray-800">Quiz Questions</h3>

        {quiz.map((q, idx) => (
          <div key={idx} className="p-4 bg-gray-50 border rounded space-y-2">
            <p className="font-medium">{q.text}</p>
            <ul className="list-disc ml-6 text-gray-700">
              {q.options.map((opt, i) => (
                <li key={i} className={opt === q.correct ? 'font-semibold text-green-600' : ''}>
                  {opt}
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setNewQuestion(q);
                  setQuiz(quiz.filter((_, i) => i !== idx));
                }}
                className="text-blue-600 text-sm hover:underline"
              >
                Edit
              </button>
              <button
                onClick={() => setQuiz(quiz.filter((_, i) => i !== idx))}
                className="text-gray-500 text-sm hover:underline"
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        <div className="border-t pt-6 space-y-4">
          <input
            type="text"
            placeholder="Question"
            value={newQuestion.text}
            onChange={(e) => setNewQuestion({ ...newQuestion, text: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded"
          />
          {newQuestion.options.map((opt, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input
                type="text"
                placeholder={`Option ${i + 1}`}
                value={opt}
                onChange={(e) => {
                  const options = [...newQuestion.options];
                  options[i] = e.target.value;
                  setNewQuestion({ ...newQuestion, options });
                }}
                className="flex-1 p-3 border border-gray-300 rounded"
              />
              {i >= 2 && (
                <button
                  onClick={() => {
                    const options = [...newQuestion.options];
                    options.splice(i, 1);
                    setNewQuestion({ ...newQuestion, options });
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          ))}
          {newQuestion.options.length < 5 && (
            <button
              onClick={() => setNewQuestion({ ...newQuestion, options: [...newQuestion.options, ''] })}
              className="text-blue-600 hover:underline text-sm"
            >
              + Add Option
            </button>
          )}
          <input
            type="text"
            placeholder="Correct Answer"
            value={newQuestion.correct}
            onChange={(e) => setNewQuestion({ ...newQuestion, correct: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded"
          />
          <button
            onClick={handleAddQuestion}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500"
          >
            Add Question
          </button>
        </div>
      </div>

      {success && <p className="text-green-600">{success}</p>}
      {error && <p className="text-red-500">{error}</p>}

      <button
        onClick={handleSubmit}
        className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-500"
      >
        Save Module Lesson
      </button>
    </div>
  );
}
